import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { rupees, fmtDate } from "@/lib/helpers";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DownloadSimple, ChartBar } from "@phosphor-icons/react";

function Metric({ label, value }) {
  return (
    <Card className="p-4 bg-white border-[#E6E2D6] shadow-sm">
      <div className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">{label}</div>
      <div className="text-xl font-medium text-[#1A1A1A] tabular-nums mt-1">{value}</div>
    </Card>
  );
}

export default function Reports() {
  const { activeBranch } = useAuth();
  const [sales, setSales] = useState(null);
  const [inv, setInv] = useState(null);
  const [disc, setDisc] = useState(null);
  const [sch, setSch] = useState(null);

  useEffect(() => {
    api.get("/reports/sales").then(({ data }) => setSales(data));
    api.get("/reports/inventory").then(({ data }) => setInv(data));
    api.get("/reports/discounts").then(({ data }) => setDisc(data));
    api.get("/reports/schemes").then(({ data }) => setSch(data));
  }, [activeBranch]);

  const exportSales = () => {
    const token = localStorage.getItem("og_token");
    fetch(`${API}/reports/sales/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "sales_report.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Reports" subtitle="Sales, inventory valuation, discount usage & scheme liability." />

      <Tabs defaultValue="sales">
        <TabsList className="bg-[#F2EBE1]">
          <TabsTrigger value="sales" data-testid="rep-tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="rep-tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="rep-tab-discounts">Discounts</TabsTrigger>
          <TabsTrigger value="schemes" data-testid="rep-tab-schemes">Schemes</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-4">
          {sales && (
            <>
              <div className="flex justify-between items-center mb-4">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 flex-1">
                  <Metric label="Invoices" value={sales.summary.count} />
                  <Metric label="Gross" value={rupees(sales.summary.gross)} />
                  <Metric label="Discount" value={rupees(sales.summary.discount)} />
                  <Metric label="GST" value={rupees(sales.summary.gst)} />
                  <Metric label="Net Sales" value={rupees(sales.summary.net)} />
                </div>
              </div>
              <Button onClick={exportSales} variant="outline" className="border-[#E6E2D6] mb-3" data-testid="export-sales"><DownloadSimple size={16} className="mr-1" /> Export CSV</Button>
              <Card className="bg-white border-[#E6E2D6] shadow-sm">
                {sales.rows.length === 0 ? <EmptyState /> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Cashier</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>{sales.rows.map((s) => (
                      <TableRow key={s.id}><TableCell className="font-medium">{s.invoice_no}</TableCell><TableCell className="text-sm">{fmtDate(s.created_at)}</TableCell><TableCell>{s.customer_name}</TableCell><TableCell className="text-sm">{s.cashier_name}</TableCell><TableCell className="text-right tabular-nums">{rupees(s.grand_total)}</TableCell><TableCell><StatusBadge status={s.status} /></TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="inventory" className="mt-4">
          {inv && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Metric label="Total Items" value={inv.total_items} />
                <Metric label="Available Weight" value={`${inv.total_weight} g`} />
                <Metric label="Valuation" value={rupees(inv.total_valuation)} />
                <Metric label="Statuses" value={inv.by_status.length} />
              </div>
              <Card className="bg-white border-[#E6E2D6] shadow-sm">
                <Table>
                  <TableHeader><TableRow><TableHead>Product</TableHead><TableHead className="text-right">Count</TableHead><TableHead className="text-right">Weight (g)</TableHead><TableHead className="text-right">Value</TableHead></TableRow></TableHeader>
                  <TableBody>{inv.by_product.map((p, i) => (
                    <TableRow key={i}><TableCell>{p.name}</TableCell><TableCell className="text-right tabular-nums">{p.count}</TableCell><TableCell className="text-right tabular-nums">{p.weight.toFixed(3)}</TableCell><TableCell className="text-right tabular-nums">{rupees(p.value)}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="discounts" className="mt-4">
          {disc && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                <Metric label="Total Discount Given" value={rupees(disc.total_discount)} />
                <Metric label="Invoices w/ Discount" value={disc.invoices_with_discount} />
                <Metric label="Approval Requests" value={disc.requests.length} />
              </div>
              <Card className="bg-white border-[#E6E2D6] shadow-sm">
                {disc.requests.length === 0 ? <EmptyState /> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Requested By</TableHead><TableHead className="text-right">%</TableHead><TableHead>Status</TableHead><TableHead>Approved By</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>{disc.requests.map((r) => (
                      <TableRow key={r.id}><TableCell>{r.requested_by_name}</TableCell><TableCell className="text-right tabular-nums">{r.requested_percent}%</TableCell><TableCell><StatusBadge status={r.status} /></TableCell><TableCell className="text-sm">{r.approved_by_name || "-"}</TableCell><TableCell className="text-sm text-[#8A8A8A]">{fmtDate(r.created_at)}</TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="schemes" className="mt-4">
          {sch && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                <Metric label="Enrollments" value={sch.total_enrollments} />
                <Metric label="Active" value={sch.active} />
                <Metric label="Matured" value={sch.matured} />
                <Metric label="Collected" value={rupees(sch.total_collected)} />
                <Metric label="Liability" value={rupees(sch.liability)} />
              </div>
              <Card className="bg-white border-[#E6E2D6] shadow-sm">
                {sch.rows.length === 0 ? <EmptyState /> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Account</TableHead><TableHead>Customer</TableHead><TableHead>Scheme</TableHead><TableHead className="text-right">Paid</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>{sch.rows.map((e) => (
                      <TableRow key={e.id}><TableCell className="font-medium">{e.account_no}</TableCell><TableCell>{e.customer_name}</TableCell><TableCell className="text-sm">{e.scheme_name}</TableCell><TableCell className="text-right tabular-nums">{rupees(e.total_paid)}</TableCell><TableCell><StatusBadge status={e.status} /></TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                )}
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
